import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

// Desktop (lg+) collapse state for the main nav rail. Separate from the mobile
// drawer open/close in Shell — this hides the permanent rail to give a screen
// more width. Ephemeral (not persisted): individual screens can default it
// collapsed on mount via `setCollapsed` (e.g. the SCP three-pane console).
type SidebarCollapseValue = {
    collapsed: boolean;
    setCollapsed: (value: boolean) => void;
    toggle: () => void;
};

const SidebarCollapseContext = createContext<SidebarCollapseValue | null>(null);

export function SidebarCollapseProvider({ children }: { children: ReactNode }) {
    const [collapsed, setCollapsed] = useState(false);
    const value = useMemo(
        () => ({ collapsed, setCollapsed, toggle: () => setCollapsed((c) => !c) }),
        [collapsed],
    );
    return (
        <SidebarCollapseContext.Provider value={value}>{children}</SidebarCollapseContext.Provider>
    );
}

export function useSidebarCollapse(): SidebarCollapseValue {
    const ctx = useContext(SidebarCollapseContext);
    if (!ctx) {
        throw new Error('useSidebarCollapse must be used within SidebarCollapseProvider');
    }
    return ctx;
}
